(() => {
  'use strict';

  const COLOR_MAP = {
    black:'#171717', white:'#f7f7f4', navy:'#1f2a44', burgundy:'#6b2638', maroon:'#6b2638', red:'#b83a3a', royal:'#3157a4', blue:'#3157a4', 'baby blue':'#a9c9e8', 'light blue':'#a9c9e8', pink:'#e9a7bb', 'baby pink':'#efbdca', purple:'#6e508b', lavender:'#b4a3ca', green:'#487458', 'forest green':'#2f543c', olive:'#74724a', yellow:'#e6c64a', orange:'#d47c3b', brown:'#755542', tan:'#c9ad83', sand:'#d4c3a3', cream:'#ece2cc', natural:'#e8ddc6', gray:'#999999', grey:'#999999', 'heather gray':'#a9a9a6', 'heather grey':'#a9a9a6', charcoal:'#4d5054'
  };

  // v14.2.1: safe client-side placement profiles. These intentionally live in
  // JavaScript so the existing, known-good Shopify product template/schema stays
  // unchanged. Additional garment and stationery profiles can be added here.
  const PREVIEW_PROFILES = {
    tee: { mode:'physical', printWidthIn:11, topOffsetIn:3, referenceWidthIn:17, bodyWidthPct:56, anchorXPct:50, anchorYPct:15, defaultColor:'Black' },
    'long-sleeve': { mode:'physical', printWidthIn:11, topOffsetIn:3, referenceWidthIn:19, bodyWidthPct:58, anchorXPct:50, anchorYPct:15, defaultColor:'Black' },
    hoodie: { mode:'physical', printWidthIn:9.5, topOffsetIn:4.5, referenceWidthIn:24, bodyWidthPct:60, anchorXPct:50, anchorYPct:16, defaultColor:'Black' },
    sweatshirt: { mode:'physical', printWidthIn:11, topOffsetIn:3.5, referenceWidthIn:21, bodyWidthPct:60, anchorXPct:50, anchorYPct:15, defaultColor:'Black' },
    stationery: { mode:'percent' },
    flat: { mode:'percent' }
  };



  // v14.2.2: normalize reusable garment photography against a stable reference
  // color (normally Black). Variant photos can contain slightly different source
  // cropping/padding, which otherwise makes the collar jump when colors change.
  // We analyze the central non-background silhouette in a small canvas and shift
  // each photo so its garment top/center matches the reference photo. If browser
  // image analysis is unavailable, the viewer safely falls back to the source image.
  const garmentBoundsCache = new Map();
  const garmentReferenceBounds = new Map();

  function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }

  function analyzeGarmentBounds(imageUrl) {
    if (!imageUrl) return Promise.resolve(null);
    if (garmentBoundsCache.has(imageUrl)) return garmentBoundsCache.get(imageUrl);
    const task = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const size = 192;
          const canvas = document.createElement('canvas');
          canvas.width = size; canvas.height = size;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return resolve(null);
          ctx.clearRect(0,0,size,size);
          ctx.drawImage(img,0,0,size,size);
          const data = ctx.getImageData(0,0,size,size).data;

          const sample = [];
          const corner = 8;
          const pushPixel = (x,y) => {
            const i=(y*size+x)*4;
            if (data[i+3] > 10) sample.push([data[i],data[i+1],data[i+2]]);
          };
          for(let y=0;y<corner;y++) for(let x=0;x<corner;x++) {
            pushPixel(x,y); pushPixel(size-1-x,y); pushPixel(x,size-1-y); pushPixel(size-1-x,size-1-y);
          }
          const bg = sample.length ? sample.reduce((a,p)=>[a[0]+p[0],a[1]+p[1],a[2]+p[2]],[0,0,0]).map(v=>v/sample.length) : [255,255,255];

          let minX=size, minY=size, maxX=-1, maxY=-1, count=0;
          const threshold = 18;
          for(let y=5;y<size-5;y++) for(let x=5;x<size-5;x++) {
            const i=(y*size+x)*4;
            const a=data[i+3];
            if (a < 20) continue;
            const dr=data[i]-bg[0], dg=data[i+1]-bg[1], db=data[i+2]-bg[2];
            const dist=Math.sqrt(dr*dr+dg*dg+db*db);
            if (dist < threshold) continue;
            minX=Math.min(minX,x); minY=Math.min(minY,y); maxX=Math.max(maxX,x); maxY=Math.max(maxY,y); count++;
          }
          if (count < size*size*0.004 || maxX<=minX || maxY<=minY) return resolve(null);
          const width=(maxX-minX+1)/size, height=(maxY-minY+1)/size;
          if (width < .2 || height < .2) return resolve(null);
          resolve({ left:minX/size, top:minY/size, right:(maxX+1)/size, bottom:(maxY+1)/size, width, height, centerX:((minX+maxX+1)/2)/size });
        } catch (_) { resolve(null); }
      };
      img.onerror=()=>resolve(null);
      img.src=imageUrl;
    });
    garmentBoundsCache.set(imageUrl, task);
    return task;
  }

  function colorForName(name, fallback) {
    const key = String(name || '').trim().toLowerCase();
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    const found = Object.keys(COLOR_MAP).find((candidate) => key.includes(candidate));
    return found ? COLOR_MAP[found] : (fallback || '#e8e8e8');
  }

  function uniq(values) { return [...new Set(values.filter(Boolean))]; }

  const FIT_LABELS = { youth:'Youth', unisex:'Unisex', women:'Women', men:'Men' };
  const FIT_ORDER = ['youth','unisex','women','men'];
  function fitLabel(key) { return FIT_LABELS[String(key || '').toLowerCase()] || String(key || ''); }

  document.querySelectorAll('[data-me-finished-product]').forEach((root) => {
    const dataNode = root.querySelector('[data-me-finished-garments]');
    if (!dataNode) return;
    let garments = [];
    try { garments = JSON.parse(dataNode.textContent || '[]'); } catch (_) { return; }
    garments = garments.filter((garment) => garment && Array.isArray(garment.variants) && garment.variants.length);

    const designVariantNode = root.querySelector('[data-me-finished-design-variants]');
    let designProduct = { options: [], variants: [] };
    if (designVariantNode) {
      try { designProduct = JSON.parse(designVariantNode.textContent || '{}') || designProduct; } catch (_) {}
    }
    designProduct.options = Array.isArray(designProduct.options) ? designProduct.options : [];
    designProduct.variants = Array.isArray(designProduct.variants) ? designProduct.variants : [];

    const garmentButtons = [...root.querySelectorAll('[data-me-garment-choice]')];
    const fitGroup = root.querySelector('[data-me-fit-group]');
    const fitChoices = root.querySelector('[data-me-fit-choices]');
    const garmentGroup = root.querySelector('[data-me-garment-group]');
    const colorGroup = root.querySelector('[data-me-color-group]');
    const colorChoices = root.querySelector('[data-me-color-choices]');
    const selectedColorLabel = root.querySelector('[data-me-selected-color]');
    const sizeGroup = root.querySelector('[data-me-size-group]');
    const sizeChoices = root.querySelector('[data-me-size-choices]');
    const variantId = root.querySelector('[data-me-finished-variant-id]');
    const price = root.querySelector('[data-me-finished-price]');
    const status = root.querySelector('[data-me-finished-status]');
    const submit = root.querySelector('[data-me-finished-submit]');
    const submitLabel = root.querySelector('[data-me-finished-submit-label]');
    const finishedForm = root.querySelector('[data-me-finished-form]');
    const previewAcknowledgement = root.querySelector('[data-me-preview-acknowledgement]');
    finishedForm?.addEventListener('submit', (event) => {
      if (!previewAcknowledgement?.checked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        previewAcknowledgement?.focus();
        previewAcknowledgement?.reportValidity();
      }
    }, true);

    const garmentImage = root.querySelector('[data-me-garment-image]');
    const fallbackGarment = root.querySelector('[data-me-fallback-garment]');
    const artwork = root.querySelector('[data-me-design-artwork]');
    const artworkMissing = root.querySelector('[data-me-artwork-missing]');
    const artworkMetafieldRender = root.querySelector('[data-me-artwork-metafield-render]');
    const propFit = root.querySelector('[data-me-property-fit]');
    const propGarment = root.querySelector('[data-me-property-garment]');
    const propColor = root.querySelector('[data-me-property-color]');
    const propSize = root.querySelector('[data-me-property-size]');
    const propBaseProduct = root.querySelector('[data-me-property-base-product]');
    const propBaseVariant = root.querySelector('[data-me-property-base-variant]');
    const propBaseGarmentImage = root.querySelector('[data-me-property-base-garment-image]');
    const propPlacementProfile = root.querySelector('[data-me-property-placement-profile]');
    const propArtwork = root.querySelector('[data-me-property-artwork]');
    const summaryFit = root.querySelector('[data-me-summary-fit]');
    const summaryStyle = root.querySelector('[data-me-summary-style]');
    const summaryColor = root.querySelector('[data-me-summary-color]');
    const summarySize = root.querySelector('[data-me-summary-size]');
    const debugArtwork = root.querySelector('[data-me-debug-artwork]');
    const debugArtworkSource = root.querySelector('[data-me-debug-artwork-source]');
    const debugGarment = root.querySelector('[data-me-debug-garment]');
    const debugVariant = root.querySelector('[data-me-debug-variant]');
    const viewLarger = root.querySelector('[data-me-view-larger]');
    const lightbox = root.querySelector('[data-me-preview-lightbox]');
    const lightboxClose = root.querySelector('[data-me-lightbox-close]');
    const lightboxGarment = root.querySelector('[data-me-lightbox-garment]');
    const lightboxFallback = root.querySelector('[data-me-lightbox-fallback]');
    const lightboxArtwork = root.querySelector('[data-me-lightbox-artwork]');
    const lightboxSelection = root.querySelector('[data-me-lightbox-selection]');
    const lightboxStage = root.querySelector('[data-me-lightbox-stage]');
    const previewFrame = root.querySelector('.me-finished__preview-frame');
    const lightboxCanvas = root.querySelector('[data-me-lightbox-canvas]');
    const zoomControls = root.querySelector('[data-me-zoom-controls]');
    const zoomIn = root.querySelector('[data-me-zoom-in]');
    const zoomOut = root.querySelector('[data-me-zoom-out]');
    const zoomReset = root.querySelector('[data-me-zoom-reset]');
    const zoomLabel = root.querySelector('[data-me-zoom-label]');

    let audience = '';
    let garment = null;
    let color = '';
    let size = '';
    let previewZoom = 1;
    let previewPanX = 0;
    let previewPanY = 0;
    let panStart = null;

    function applyLightboxZoom(nextZoom, resetPan = false) {
      previewZoom = Math.min(2, Math.max(1, Number(nextZoom) || 1));
      if (resetPan || previewZoom <= 1) { previewPanX = 0; previewPanY = 0; }
      if (lightboxCanvas) {
        lightboxCanvas.style.setProperty('--me-zoom', previewZoom.toFixed(2));
        lightboxCanvas.style.setProperty('--me-pan-x', `${previewPanX}px`);
        lightboxCanvas.style.setProperty('--me-pan-y', `${previewPanY}px`);
      }
      if (zoomLabel) zoomLabel.textContent = `${Math.round(previewZoom * 100)}%`;
      lightboxStage?.classList.toggle('is-zoomed', previewZoom > 1.001);
    }

    // v14.4.9: keep the modal's 100% composition on the exact same aspect
    // ratio as the live preview frame. Artwork placement values are percentage
    // based, so allowing the modal stage to become a different rectangle makes
    // the design look larger/smaller relative to the garment even when the
    // underlying placement settings are identical.
    function syncLightboxCanvasGeometry() {
      if (!lightboxCanvas || !lightboxStage || !previewFrame) return;
      const previewRect = previewFrame.getBoundingClientRect();
      const stageRect = lightboxStage.getBoundingClientRect();
      if (!previewRect.width || !previewRect.height || !stageRect.width || !stageRect.height) return;

      const ratio = previewRect.width / previewRect.height;
      let width = stageRect.width;
      let height = width / ratio;
      if (height > stageRect.height) {
        height = stageRect.height;
        width = height * ratio;
      }
      lightboxCanvas.style.width = `${width}px`;
      lightboxCanvas.style.height = `${height}px`;
    }

    function resolveArtworkFromMetafieldTag() {
      if (!artwork || !artworkMetafieldRender) return false;
      const renderedImage = artworkMetafieldRender.querySelector('img');
      const renderedLink = artworkMetafieldRender.querySelector('a[href]');
      const src = renderedImage?.currentSrc || renderedImage?.getAttribute('src') || renderedLink?.getAttribute('href') || '';
      if (!src) return false;

      artwork.src = src;
      artwork.hidden = false;
      if (artworkMissing) artworkMissing.hidden = true;
      if (propArtwork) propArtwork.value = src;
      root.dataset.artworkSource = 'metafield-tag';
      if (debugArtworkSource) debugArtworkSource.textContent = 'metafield-tag';
      if (debugArtwork) debugArtwork.textContent = 'loading';
      return true;
    }

    // Shopify's metafield_tag filter is the most reliable normalizer for a
    // file_reference image. It renders the CDN-backed <img> regardless of
    // whether Liquid exposes the underlying value as Image or MediaImage.
    if (!artwork?.getAttribute('src')) resolveArtworkFromMetafieldTag();

    function optionIndex(names, needles, fallbackIndex) {
      const normalized = (names || []).map((n) => String(n).toLowerCase());
      const idx = normalized.findIndex((name) => needles.some((needle) => name.includes(needle)));
      return idx >= 0 ? idx : fallbackIndex;
    }

    function indexes() {
      const names = garment?.options || [];
      return {
        color: optionIndex(names, ['color','colour'], 0),
        size: optionIndex(names, ['size'], Math.min(1, Math.max(0, names.length - 1)))
      };
    }

    function profileForGarment() {
      if (!garment) return null;
      const key = String(garment.profileKey || garment.key || '').toLowerCase();
      return PREVIEW_PROFILES[key] || null;
    }

    function updateArtworkPlacement() {
      if (!artwork || !garment) return;
      const profile = profileForGarment();
      const useCustom = String(garment.placementMode || '').toLowerCase() === 'custom';

      // v14.5.0: placement calibration belongs to the reusable BASE product.
      // Custom mode makes the block's width/X/Y authoritative so one hoodie
      // can be calibrated once and every finished design inherits it.
      if (useCustom) {
        artwork.style.setProperty('--me-art-width', `${Number(garment.artworkWidth || 48)}%`);
        artwork.style.setProperty('--me-art-x', `${Number(garment.artworkX || 50)}%`);
        artwork.style.setProperty('--me-art-y', `${Number(garment.artworkY || 39)}%`);
        artwork.dataset.placementMode = 'center-anchor';
      } else if (profile?.mode === 'physical') {
        const widthPct = (Number(profile.printWidthIn) / Number(profile.referenceWidthIn)) * Number(profile.bodyWidthPct);
        const topPct = Number(profile.anchorYPct) + ((Number(profile.topOffsetIn) / Number(profile.referenceWidthIn)) * Number(profile.bodyWidthPct));
        artwork.style.setProperty('--me-art-width', `${widthPct}%`);
        artwork.style.setProperty('--me-art-x', `${Number(profile.anchorXPct)}%`);
        artwork.style.setProperty('--me-art-y', `${topPct}%`);
        artwork.dataset.placementMode = 'top-anchor';
      } else {
        artwork.style.setProperty('--me-art-width', `${Number(garment.artworkWidth || 48)}%`);
        artwork.style.setProperty('--me-art-x', `${Number(garment.artworkX || 50)}%`);
        artwork.style.setProperty('--me-art-y', `${Number(garment.artworkY || 39)}%`);
        artwork.dataset.placementMode = 'center-anchor';
      }
    }

    function applyGarmentCalibration(imageEl) {
      if (!imageEl || !garment) return;
      imageEl.style.setProperty('--me-garment-scale', String(clamp(Number(garment.garmentScale || 100) / 100, .8, 1.2)));
      imageEl.style.setProperty('--me-garment-cal-x', `${clamp(Number(garment.garmentX || 0), -15, 15)}%`);
      imageEl.style.setProperty('--me-garment-cal-y', `${clamp(Number(garment.garmentY || 0), -15, 15)}%`);
    }

    function normalizeChoice(value) {
      return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    function designOptionIndex(needles) {
      const normalized = designProduct.options.map((name) => normalizeChoice(name));
      return normalized.findIndex((name) => needles.some((needle) => name.includes(needle)));
    }

    function purchaseVariantForGarment(targetGarment = garment, targetAudience = audience) {
      const variants = designProduct.variants || [];
      if (!variants.length) return null;
      if (variants.length === 1) return variants[0];

      const fitIdx = designOptionIndex(['fit','audience','age group','age']);
      const styleIdx = designOptionIndex(['style','garment','product type','product']);
      const wantedFit = normalizeChoice(fitLabel(targetAudience));
      const wantedStyle = normalizeChoice(targetGarment?.label);

      let matches = variants.filter((variant) => {
        const options = Array.isArray(variant.options) ? variant.options.map(normalizeChoice) : [];
        if (fitIdx >= 0 && wantedFit && options[fitIdx] !== wantedFit) return false;
        if (styleIdx >= 0 && wantedStyle && options[styleIdx] !== wantedStyle) return false;
        return true;
      });

      // If the design product has not used the recommended option names, allow
      // an exact value match anywhere in the variant options. This keeps the
      // engine tolerant of Fit/Style option order while avoiding color/size
      // expansion on the customer-facing design product.
      if (!matches.length) {
        matches = variants.filter((variant) => {
          const options = Array.isArray(variant.options) ? variant.options.map(normalizeChoice) : [];
          const fitOkay = !wantedFit || options.includes(wantedFit);
          const styleOkay = !wantedStyle || options.includes(wantedStyle);
          return fitOkay && styleOkay;
        });
      }
      return matches[0] || null;
    }

    function updateGarmentButtonPrices() {
      garmentButtons.forEach((button) => {
        const item = garments.find((entry) => String(entry.key) === String(button.dataset.garmentKey));
        const itemAudience = String(item?.audience || button.dataset.audience || '').toLowerCase();
        const purchase = purchaseVariantForGarment(item, itemAudience);
        const small = button.querySelector('small');
        if (!small) return;
        small.textContent = purchase?.priceFormatted || 'Price not configured';
      });
    }

    function variantsForColor(value) {
      if (!garment) return [];
      const idx = indexes().color;
      return garment.variants.filter((v) => String(v.options[idx]) === String(value));
    }

    function chosenBaseVariant() {
      if (!garment || !color || !size) return null;
      const idx = indexes();
      return garment.variants.find((v) => String(v.options[idx.color]) === String(color) && String(v.options[idx.size]) === String(size)) || null;
    }



    function referenceImageUrlForGarment() {
      if (!garment) return '';
      const profile = profileForGarment();
      const idx = indexes().color;
      const preferred = profile?.defaultColor || 'Black';
      return garment.variants.find((v) => String(v.options[idx]) === String(preferred) && v.featuredImage)?.featuredImage
        || garment.variants.find((v) => v.featuredImage)?.featuredImage || '';
    }

    async function normalizeGarmentPhoto(imageEl, imageUrl) {
      if (!imageEl || !imageUrl || !garment) return;
      applyGarmentCalibration(imageEl);
      const refUrl = referenceImageUrlForGarment();
      if (!refUrl) return;
      const refKey = `${garment.key || garment.handle || 'garment'}::${refUrl}`;
      let refBounds = garmentReferenceBounds.get(refKey);
      if (!refBounds) {
        refBounds = await analyzeGarmentBounds(refUrl);
        if (refBounds) garmentReferenceBounds.set(refKey, refBounds);
      }
      const currentBounds = imageUrl === refUrl ? refBounds : await analyzeGarmentBounds(imageUrl);
      if (!refBounds || !currentBounds || !imageEl.isConnected) {
        imageEl.style.removeProperty('--me-garment-shift-x');
        imageEl.style.removeProperty('--me-garment-shift-y');
        return;
      }

      // Keep the photography scale untouched; only remove crop/padding drift.
      // This is intentionally conservative because the actual garment image is
      // still the visual source of truth. Clamp protects against unusual photos.
      const box = imageEl.getBoundingClientRect();
      const dx = clamp((refBounds.centerX - currentBounds.centerX) * box.width, -box.width*.08, box.width*.08);
      const dy = clamp((refBounds.top - currentBounds.top) * box.height, -box.height*.10, box.height*.10);
      imageEl.style.setProperty('--me-garment-shift-x', `${dx.toFixed(2)}px`);
      imageEl.style.setProperty('--me-garment-shift-y', `${dy.toFixed(2)}px`);
      imageEl.dataset.normalizedTo = profileForGarment()?.defaultColor || 'reference';
    }

    function updatePreview(variant) {
      if (!garment) return;
      updateArtworkPlacement();
      const imageUrl = variant?.featuredImage || variantsForColor(color).find((v) => v.featuredImage)?.featuredImage || null;
      if (debugGarment) debugGarment.textContent = garment.label || garment.key || 'selected';

      if (imageUrl && garmentImage) {
        if (fallbackGarment) fallbackGarment.hidden = true;
        if (debugVariant) debugVariant.textContent = 'loading real garment image';
        if (garmentImage.src !== imageUrl) {
          garmentImage.hidden = true;
          garmentImage.classList.add('is-changing');
          const preload = new Image();
          preload.onload = () => {
            garmentImage.src = imageUrl;
            garmentImage.hidden = false;
            garmentImage.classList.remove('is-changing');
            if (fallbackGarment) fallbackGarment.hidden = true;
            normalizeGarmentPhoto(garmentImage, imageUrl);
            if (debugVariant) debugVariant.textContent = 'real garment image loaded · frame normalized';
          };
          preload.onerror = () => {
            garmentImage.hidden = true;
            garmentImage.classList.remove('is-changing');
            if (fallbackGarment) {
              fallbackGarment.hidden = false;
              fallbackGarment.style.color = colorForName(color, garment.fallbackColor);
            }
            if (debugVariant) debugVariant.textContent = 'real image failed; placeholder active';
          };
          preload.src = imageUrl;
        } else {
          garmentImage.hidden = false;
          if (fallbackGarment) fallbackGarment.hidden = true;
          normalizeGarmentPhoto(garmentImage, imageUrl);
          if (debugVariant) debugVariant.textContent = 'real garment image loaded · frame normalized';
        }
      } else {
        if (garmentImage) garmentImage.hidden = true;
        if (fallbackGarment) {
          fallbackGarment.hidden = false;
          fallbackGarment.style.color = colorForName(color, garment.fallbackColor);
          fallbackGarment.dataset.garment = garment.key || '';
        }
        if (debugVariant) debugVariant.textContent = 'no real image; placeholder active';
      }
    }

    function renderSizes() {
      if (!sizeChoices || !garment || !color) return;
      const idx = indexes();
      const values = uniq(variantsForColor(color).map((v) => v.options[idx.size]));
      if (!values.includes(size)) size = '';
      sizeChoices.innerHTML = '';
      values.forEach((value) => {
        const candidates = variantsForColor(color).filter((v) => String(v.options[idx.size]) === String(value));
        const available = candidates.some((v) => v.available);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'me-finished__size';
        button.textContent = value;
        button.disabled = !available;
        button.dataset.value = value;
        button.setAttribute('aria-pressed', String(size === value));
        if (size === value) button.classList.add('is-selected');
        button.addEventListener('click', () => { size = value; renderSizes(); applySelection(); });
        sizeChoices.appendChild(button);
      });
      sizeGroup.hidden = !values.length;
    }

    function renderColors() {
      if (!colorChoices || !garment) return;
      const idx = indexes();
      const values = uniq(garment.variants.map((v) => v.options[idx.color]));
      if (!values.includes(color)) color = '';
      colorChoices.innerHTML = '';
      values.forEach((value) => {
        const available = garment.variants.some((v) => String(v.options[idx.color]) === String(value) && v.available);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'me-finished__swatch';
        button.dataset.value = value;
        button.disabled = !available;
        button.setAttribute('aria-label', value);
        button.setAttribute('title', value);
        button.setAttribute('aria-pressed', String(color === value));
        button.style.setProperty('--swatch', colorForName(value, garment.fallbackColor));
        button.innerHTML = `<span class="me-finished__swatch-dot"></span><span>${value}</span>`;
        if (color === value) button.classList.add('is-selected');
        button.addEventListener('click', () => {
          color = value; size = '';
          renderColors(); renderSizes(); applySelection();
        });
        colorChoices.appendChild(button);
      });
      colorGroup.hidden = !values.length;
      if (selectedColorLabel) selectedColorLabel.textContent = color ? `— ${color}` : '';
    }

    function applySelection() {
      // This is display-only. Shopify's actual line item continues to use the
      // existing design-product variant and base-garment production metadata.
      if (summaryFit) summaryFit.textContent = audience ? fitLabel(audience) : 'Choose fit';
      if (summaryStyle) summaryStyle.textContent = garment?.label || 'Choose style';
      if (summaryColor) summaryColor.textContent = color || 'Choose color';
      if (summarySize) summarySize.textContent = size || 'Choose size';
      const baseVariant = chosenBaseVariant();
      const purchaseVariant = purchaseVariantForGarment();
      if (propFit) propFit.value = audience ? fitLabel(audience) : '';
      if (propGarment) propGarment.value = garment?.label || '';
      if (propColor) propColor.value = color || '';
      if (propSize) propSize.value = size || '';
      if (propBaseProduct) propBaseProduct.value = garment?.productTitle || garment?.handle || '';
      if (propBaseVariant) propBaseVariant.value = baseVariant?.id ? String(baseVariant.id) : '';
      if (propBaseGarmentImage) propBaseGarmentImage.value = baseVariant?.featuredImage || variantsForColor(color).find((item) => item.featuredImage)?.featuredImage || '';
      if (propPlacementProfile) propPlacementProfile.value = garment?.profileKey || '';
      updatePreview(baseVariant);
      updateGarmentButtonPrices();

      // v14.4.0: the customer-facing DESIGN product is now the actual Shopify
      // line item. The reusable Finished Base variant remains private production
      // metadata and supplies colors, sizes, imagery and placement calibration.
      // A design product can start with one default variant. As more garment
      // prices are added, use Fit + Style variants on the design product only.
      if (!garment || !color || !size || !baseVariant) {
        if (variantId) variantId.value = '';
        if (submit) submit.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Choose options';
        if (status) {
          status.hidden = false;
          status.textContent = !audience ? 'Choose a fit to continue.' : (!garment ? 'Choose a style to continue.' : (!color ? 'Choose a color to continue.' : 'Choose a size to continue.'));
        }
        if (price) {
          const mapped = garment ? purchaseVariantForGarment() : null;
          const fallback = designProduct.variants[0] || null;
          const shown = mapped || (designProduct.variants.length === 1 ? fallback : null);
          price.textContent = shown?.priceFormatted || 'Choose a style';
        }
        return;
      }

      if (!purchaseVariant) {
        if (variantId) variantId.value = '';
        if (submit) submit.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Style not configured';
        if (status) {
          status.hidden = false;
          status.textContent = 'This fit and style needs a matching variant on the design product before it can be purchased.';
        }
        if (price) price.textContent = 'Not configured';
        return;
      }

      variantId.value = String(purchaseVariant.id);
      if (price) price.textContent = purchaseVariant.priceFormatted;
      const purchasable = !!baseVariant.available && !!purchaseVariant.available;
      if (submit) submit.disabled = !purchasable;
      if (submitLabel) submitLabel.textContent = purchasable ? 'Add to cart' : 'Sold out';
      if (status) {
        status.hidden = purchasable;
        status.textContent = purchasable ? '' : 'This combination is currently unavailable.';
      }
    }

    function chooseGarment(key) {
      const previousColor = color;
      garment = garments.find((item) => String(item.key) === String(key) && (!audience || String(item.audience || '').toLowerCase() === audience)) || null;
      const profile = profileForGarment();
      const colorIdx = indexes().color;
      const availableColors = uniq((garment?.variants || []).map((v) => v.options[colorIdx]));
      const preferred = profile?.defaultColor || 'Black';
      color = previousColor && availableColors.includes(previousColor)
        ? previousColor
        : (availableColors.includes(preferred) ? preferred : (availableColors[0] || ''));
      size = '';
      garmentButtons.forEach((button) => {
        const sameAudience = !audience || String(button.dataset.audience || '').toLowerCase() === audience;
        button.hidden = !sameAudience;
        const selected = garment && button.dataset.garmentKey === String(garment.key);
        button.classList.toggle('is-selected', !!selected);
        button.setAttribute('aria-pressed', String(!!selected));
      });
      renderColors();
      renderSizes();
      applySelection();
    }

    function renderFitChoices() {
      if (!fitChoices) return;
      const fits = uniq(garments.map((item) => String(item.audience || 'youth').toLowerCase()))
        .sort((a,b) => FIT_ORDER.indexOf(a) - FIT_ORDER.indexOf(b));
      fitChoices.innerHTML = '';
      fits.forEach((fit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'me-finished__fit-choice';
        button.dataset.value = fit;
        button.textContent = fitLabel(fit);
        button.setAttribute('aria-pressed', String(audience === fit));
        if (audience === fit) button.classList.add('is-selected');
        button.addEventListener('click', () => chooseAudience(fit));
        fitChoices.appendChild(button);
      });
      if (fitGroup) fitGroup.hidden = !fits.length;
      if (garmentGroup) garmentGroup.hidden = !audience;
      return fits;
    }

    function chooseAudience(fit) {
      audience = String(fit || '').toLowerCase();
      garment = null; color = ''; size = '';
      const fits = renderFitChoices() || [];
      garmentButtons.forEach((button) => {
        const show = String(button.dataset.audience || 'youth').toLowerCase() === audience;
        button.hidden = !show;
        button.classList.remove('is-selected');
        button.setAttribute('aria-pressed','false');
      });
      if (garmentGroup) garmentGroup.hidden = false;
      if (colorGroup) colorGroup.hidden = true;
      if (sizeGroup) sizeGroup.hidden = true;
      const matching = garments.filter((item) => String(item.audience || 'youth').toLowerCase() === audience);
      // v14.5.0: never leave the customer staring at artwork over a placeholder.
      // A fit selection always initializes its first real garment style + color.
      if (matching.length) chooseGarment(matching[0].key);
      else applySelection();
    }

    function syncLightbox() {
      if (!lightbox) return;
      const garmentSrc = garmentImage && !garmentImage.hidden ? garmentImage.currentSrc || garmentImage.src : '';
      if (garmentSrc && lightboxGarment) {
        lightboxGarment.src = garmentSrc;
        lightboxGarment.hidden = false;
        lightboxGarment.style.setProperty('--me-garment-shift-x', garmentImage.style.getPropertyValue('--me-garment-shift-x') || '0px');
        lightboxGarment.style.setProperty('--me-garment-shift-y', garmentImage.style.getPropertyValue('--me-garment-shift-y') || '0px');
        lightboxGarment.style.setProperty('--me-garment-scale', garmentImage.style.getPropertyValue('--me-garment-scale') || '1');
        lightboxGarment.style.setProperty('--me-garment-cal-x', garmentImage.style.getPropertyValue('--me-garment-cal-x') || '0%');
        lightboxGarment.style.setProperty('--me-garment-cal-y', garmentImage.style.getPropertyValue('--me-garment-cal-y') || '0%');
        if (lightboxFallback) lightboxFallback.hidden = true;
      } else if (lightboxFallback) {
        lightboxFallback.hidden = false;
        lightboxFallback.style.color = colorForName(color, garment?.fallbackColor);
        if (lightboxGarment) lightboxGarment.hidden = true;
      }
      if (lightboxArtwork && artwork && !artwork.hidden && artwork.getAttribute('src')) {
        lightboxArtwork.src = artwork.currentSrc || artwork.src;
        lightboxArtwork.style.cssText = artwork.style.cssText;
        lightboxArtwork.dataset.placementMode = artwork.dataset.placementMode || 'center-anchor';
        lightboxArtwork.hidden = false;
      } else if (lightboxArtwork) lightboxArtwork.hidden = true;
      if (lightboxSelection) lightboxSelection.textContent = [audience ? fitLabel(audience) : '', garment?.label, color, size].filter(Boolean).join(' · ');
    }

    function openLightbox() {
      if (!lightbox) return;
      syncLightbox();
      applyLightboxZoom(1, true);
      lightbox.hidden = false;
      document.documentElement.classList.add('me-preview-open');
      // The stage must be visible before it has measurable dimensions.
      requestAnimationFrame(() => {
        syncLightboxCanvasGeometry();
        applyLightboxZoom(1, true);
      });
      lightboxClose?.focus();
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      applyLightboxZoom(1, true);
      document.documentElement.classList.remove('me-preview-open');
      viewLarger?.focus();
    }
    viewLarger?.addEventListener('click', openLightbox);
    lightboxClose?.addEventListener('click', closeLightbox);
    lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) closeLightbox(); });
    // v14.4.8: keep zoom controls completely outside the stage pan gesture.
    // Some browsers suppress a button click when its pointer sequence bubbles
    // into a touch-action:none / pointer-capture surface. Stop the pointer
    // sequence at the control pill and delegate the click there.
    zoomControls?.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
    });
    zoomControls?.addEventListener('pointerup', (event) => {
      event.stopPropagation();
    });
    zoomControls?.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      event.stopPropagation();
      if (button.matches('[data-me-zoom-in]')) applyLightboxZoom(previewZoom + .25);
      else if (button.matches('[data-me-zoom-out]')) applyLightboxZoom(previewZoom - .25);
      else if (button.matches('[data-me-zoom-reset]')) applyLightboxZoom(1, true);
    });
    lightboxStage?.addEventListener('wheel', (event) => {
      if (!lightbox || lightbox.hidden) return;
      event.preventDefault();
      applyLightboxZoom(previewZoom + (event.deltaY < 0 ? .12 : -.12));
    }, { passive: false });
    lightboxStage?.addEventListener('pointerdown', (event) => {
      // Zoom controls live inside the stage. Once zoomed, do not let the
      // stage's pan handler capture pointer events that belong to those buttons.
      if (event.target.closest?.('.me-finished__zoom-controls')) return;
      if (previewZoom <= 1 || event.button !== 0) return;
      panStart = { x:event.clientX, y:event.clientY, panX:previewPanX, panY:previewPanY, id:event.pointerId };
      lightboxStage.setPointerCapture?.(event.pointerId);
      lightboxStage.classList.add('is-panning');
    });
    lightboxStage?.addEventListener('pointermove', (event) => {
      if (!panStart || panStart.id !== event.pointerId) return;
      const maxPan = 180 * previewZoom;
      previewPanX = Math.max(-maxPan, Math.min(maxPan, panStart.panX + event.clientX - panStart.x));
      previewPanY = Math.max(-maxPan, Math.min(maxPan, panStart.panY + event.clientY - panStart.y));
      applyLightboxZoom(previewZoom);
    });
    const stopPan = (event) => {
      if (!panStart || (event.pointerId != null && panStart.id !== event.pointerId)) return;
      panStart = null;
      lightboxStage?.classList.remove('is-panning');
    };
    lightboxStage?.addEventListener('pointerup', stopPan);
    lightboxStage?.addEventListener('pointercancel', stopPan);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
      if (lightbox && !lightbox.hidden && (event.key === '+' || event.key === '=')) applyLightboxZoom(previewZoom + .25);
      if (lightbox && !lightbox.hidden && event.key === '-') applyLightboxZoom(previewZoom - .25);
      if (lightbox && !lightbox.hidden && event.key === '0') applyLightboxZoom(1, true);
    });

    window.addEventListener('resize', () => {
      if (lightbox && !lightbox.hidden) syncLightboxCanvasGeometry();
    });

    garmentButtons.forEach((button) => button.addEventListener('click', () => chooseGarment(button.dataset.garmentKey)));

    if (artwork?.getAttribute('src')) {
      if (debugArtwork) debugArtwork.textContent = artwork.complete && artwork.naturalWidth > 0 ? 'loaded' : 'loading';
      artwork.addEventListener('load', () => {
        artwork.hidden = false;
        if (artworkMissing) artworkMissing.hidden = true;
        if (debugArtwork) debugArtwork.textContent = 'loaded';
      });
      artwork.addEventListener('error', () => {
        if (debugArtwork) debugArtwork.textContent = 'failed to load';
      });
    } else {
      if (artwork) artwork.hidden = true;
      if (artworkMissing) artworkMissing.hidden = false;
      if (debugArtwork) debugArtwork.textContent = 'missing';
    }

    updateGarmentButtonPrices();
    const availableFits = renderFitChoices() || [];
    if (availableFits.length) {
      const requestedDefaultFit = String(root.dataset.meDefaultFit || root.getAttribute('data-me-default-fit') || 'unisex').toLowerCase();
      const initialFit = requestedDefaultFit !== 'automatic' && availableFits.includes(requestedDefaultFit)
        ? requestedDefaultFit
        : availableFits[0];
      chooseAudience(initialFit);
    } else {
      garmentButtons.forEach((button) => { button.hidden = true; });
      if (garmentGroup) garmentGroup.hidden = true;
      applySelection();
    }
  });
})();
